import { useState, useEffect, useRef, useCallback } from 'react';
import { captureApi } from '../lib/api';
import AllSitesLink from '../components/AllSitesLink';
import StaffSignIn from '../components/StaffSignIn';
import useStaffSession from '../hooks/useStaffSession';
import { readFileAsDataUrl, resizeToJpeg } from '../lib/images';

// Mobile-first receipt capture for field staff (receipt_capturer role), behind
// the shared staff sign-in (hooks/useStaffSession.js). The start page only
// hands this site a sign-in for roles allowed on it.
// The reason field is required at capture time — that's what keeps the
// "receipt with no reason" flag rare instead of the norm.

// The phone's own calendar day: toISOString() is UTC, which in Colorado turns
// over at 6pm and dated evening receipts tomorrow.
const today = () => new Date().toLocaleDateString('en-CA');
const EMPTY = { amount: '', reason: '', merchant: '', purchaseDate: today() };

export default function ReceiptCapture() {
  const session = useStaffSession('receipts');
  const { token } = session;

  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);      // resized JPEG data URL
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [justSaved, setJustSaved] = useState(null);
  // Which fields came off the photo rather than from the capturer's thumbs, so
  // the UI can say so. Cleared as soon as a field is edited by hand.
  const [readFields, setReadFields] = useState({});
  const [reading, setReading] = useState(false);
  const [mine, setMine] = useState([]);
  const fileRef = useRef(null);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setReadFields(r => (r[k] ? { ...r, [k]: false } : r));
  };

  const loadMine = useCallback(() => {
    captureApi.mine().then(r => setMine(r.receipts || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (token) loadMine();
  }, [token, loadMine]);

  async function handleFile(file) {
    if (!file) return;
    setError('');
    let resized;
    try {
      const raw = await readFileAsDataUrl(file);
      // Receipts need to stay legible (line items, totals): wider max edge,
      // moderate quality — typically 150–400 KB, well under the 10 MB cap.
      resized = await resizeToJpeg(raw, 1400, 0.72);
      setPhoto(resized);
    } catch (e) { setError(e.message); return; }

    // Read the receipt to fill in what it says. Only ever fills fields the
    // capturer has left blank — never overwrites something already typed — and
    // a failure is silent, because the manual flow underneath still works.
    setReading(true);
    try {
      const read = await captureApi.extract(resized);
      if (!read || !read.read) return;
      setForm(f => {
        const next = { ...f };
        const filled = {};
        if (read.merchant && !f.merchant.trim()) { next.merchant = read.merchant; filled.merchant = true; }
        if (read.amount != null && !String(f.amount).trim()) { next.amount = String(read.amount); filled.amount = true; }
        if (read.purchaseDate && f.purchaseDate === today()) { next.purchaseDate = read.purchaseDate; filled.purchaseDate = true; }
        setReadFields(filled);
        return next;
      });
    } catch {
      /* reading is a convenience; typing it in is still the real path */
    } finally {
      setReading(false);
    }
  }

  async function handleSubmit() {
    setError('');
    if (!photo) return setError('Add a photo of the receipt.');
    const amt = Number(form.amount);
    if (!Number.isFinite(amt) || amt <= 0) return setError('Enter the amount charged.');
    if (!form.reason.trim()) return setError('Add a short reason (what was this for?).');
    setBusy(true);
    try {
      const res = await captureApi.capture({
        image: photo,
        amount: amt,
        reason: form.reason.trim(),
        merchant: form.merchant.trim(),
        purchaseDate: form.purchaseDate,
      });
      setJustSaved(res);
      setForm({ ...EMPTY, purchaseDate: form.purchaseDate });
      setPhoto(null);
      if (fileRef.current) fileRef.current.value = '';
      loadMine();
      setTimeout(() => setJustSaved(null), 4000);
    } catch (e) { setError(e.message || 'Could not save the receipt.'); }
    finally { setBusy(false); }
  }

  if (!token) return <StaffSignIn session={session} siteName="Receipts" />;

  // ── Capture screen ──
  return (
    <div style={s.page}>
      <div style={s.shell}>
        <div style={s.header}>
          <img src="/logo-full.png" alt="Silver Service" style={s.headerLogo} />
          <div>
            <AllSitesLink style={{ ...s.signOut, marginRight: 12 }} />
            <button style={s.signOut} onClick={session.signOut} title="Signs this browser out of every staff site">Sign out</button>
          </div>
        </div>
        <h2 style={s.title}>New receipt</h2>

        {justSaved && (
          <div style={s.saved}>
            ✓ Saved {money(justSaved.amount)} — {justSaved.reason}
          </div>
        )}

        <div style={s.card}>
        <label style={s.photoBox} htmlFor="receipt-photo">
          {photo
            ? <img src={photo} alt="receipt preview" style={s.preview} />
            : <div style={s.photoPrompt}><div style={s.cameraIcon}>📷</div>Tap to photograph the receipt</div>}
        </label>
        <input id="receipt-photo" ref={fileRef} type="file" accept="image/*" capture="environment"
          onChange={e => handleFile(e.target.files?.[0])} style={{ display: 'none' }} />
        {photo && <button style={s.retake} onClick={() => fileRef.current?.click()}>Retake photo</button>}

        {reading && <div style={s.reading}>Reading the receipt…</div>}
        {!reading && Object.values(readFields).some(Boolean) && (
          <div style={s.readNote}>
            Filled in from the photo — <strong>check it against the receipt</strong> before saving.
          </div>
        )}

        <label style={s.label}>Amount charged</label>
        <div style={s.amountWrap}>
          <span style={s.dollar}>$</span>
          <input style={s.amountInput} type="number" inputMode="decimal" placeholder="0.00"
            value={form.amount} onChange={e => set('amount', e.target.value)} />
        </div>

        <label style={s.label}>Reason / what was this for?</label>
        <input style={s.input} placeholder="e.g. filters for machine 3" value={form.reason}
          onChange={e => set('reason', e.target.value)} />

        <label style={s.label}>Merchant <span style={s.optional}>(optional)</span></label>
        <input style={s.input} placeholder="e.g. Grainger" value={form.merchant}
          onChange={e => set('merchant', e.target.value)} />

        <label style={s.label}>Date of transaction</label>
        <input style={s.input} type="date" value={form.purchaseDate}
          onChange={e => set('purchaseDate', e.target.value)} />

        {error && <div style={s.error}>{error}</div>}
        <button style={s.btnPrimary} onClick={handleSubmit} disabled={busy}>
          {busy ? 'Saving…' : 'Save receipt'}
        </button>
        </div>

        {mine.length > 0 && (
          <div style={s.recent}>
            <div style={s.recentTitle}>Your recent receipts</div>
            {mine.slice(0, 10).map(r => (
              <div key={r.receiptId} style={s.recentRow}>
                <span style={s.recentDate}>{r.purchaseDate}</span>
                <span style={s.recentReason}>{r.reason}</span>
                <span style={s.recentAmt}>{money(r.amount)}</span>
              </div>
            ))}
          </div>
        )}

        {showBookmarkHint() && (
          <p style={s.bookmark}>
            Next time, go straight to{' '}
            <a href={CAPTURE_URL} style={s.bookmarkUrl}>receipts.silverservice.com</a>
            {' '}— bookmark it or add it to your phone's home screen.
          </p>
        )}
      </div>
    </div>
  );
}

const CAPTURE_URL = 'https://receipts.silverservice.com';

// Staff reach this app from several places (a setup link on the orders
// domain, the admin portal's Receipt Capture button), and then can't find it
// again. Point them at the short URL — but only on the real domains, so
// staging and localhost don't advertise prod.
function showBookmarkHint() {
  const host = (typeof window !== 'undefined' && window.location.hostname) || '';
  return /\.silverservice\.com$/i.test(host) && !/^receipts\./i.test(host);
}

function money(n) {
  const v = Number(n);
  return Number.isFinite(v) ? `$${v.toFixed(2)}` : '';
}

// Palette mirrors the customer ordering platform (Login.jsx): light surfaces,
// white cards, burgundy primary, Inter — so field staff land on something that
// looks like the rest of Silver Service rather than a separate dark app.
const BURGUNDY = '#A50B18';
const s = {
  reading: { background: '#eef2ff', color: '#3340a0', borderRadius: 8, padding: '0.55rem 0.8rem', fontSize: '0.85rem', margin: '0.75rem 0 0' },
  readNote: { background: '#fff8e1', color: '#8a5a00', border: '1px solid #ffe0a3', borderRadius: 8, padding: '0.55rem 0.8rem', fontSize: '0.85rem', margin: '0.75rem 0 0' },
  page: { minHeight: '100vh', background: '#f5f5f5', color: '#333', fontFamily: "'Inter', sans-serif", padding: '16px', boxSizing: 'border-box' },
  shell: { maxWidth: 480, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  headerLogo: { height: 40, width: 'auto' },
  signOut: { background: 'none', border: 'none', color: '#888', fontSize: '0.85rem', cursor: 'pointer' },
  title: { fontSize: '1.5rem', margin: '4px 0 16px', fontWeight: 600, color: '#333' },
  card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 20, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' },
  label: { display: 'block', fontSize: '0.82rem', color: 'rgba(0,0,0,0.5)', fontWeight: 500, margin: '14px 0 6px' },
  optional: { color: 'rgba(0,0,0,0.35)', fontWeight: 400 },
  input: { width: '100%', boxSizing: 'border-box', background: '#fff', border: '1px solid #ddd', borderRadius: 8, color: '#333', padding: '13px 14px', fontSize: '1rem', marginBottom: 4, outline: 'none' },
  amountWrap: { display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: '0 14px' },
  dollar: { color: BURGUNDY, fontSize: '1.2rem', marginRight: 4, fontWeight: 600 },
  amountInput: { flex: 1, background: 'transparent', border: 'none', color: '#333', padding: '13px 0', fontSize: '1.2rem', outline: 'none' },
  photoBox: { display: 'block', width: '100%', minHeight: 180, background: '#fafafa', border: '2px dashed #ccc', borderRadius: 12, cursor: 'pointer', overflow: 'hidden', boxSizing: 'border-box' },
  photoPrompt: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 180, color: '#999', textAlign: 'center', gap: 8 },
  cameraIcon: { fontSize: '2.4rem' },
  preview: { width: '100%', display: 'block' },
  retake: { width: '100%', background: '#fff', border: '1px solid #ddd', color: '#555', borderRadius: 8, padding: '10px', marginTop: 8, cursor: 'pointer' },
  btnPrimary: { width: '100%', background: BURGUNDY, color: '#fff', border: 'none', borderRadius: 8, padding: '15px', fontSize: '1.05rem', fontWeight: 700, marginTop: 18, cursor: 'pointer', letterSpacing: '0.03em' },
  error: { background: 'rgba(231,76,60,0.06)', border: '1px solid rgba(231,76,60,0.2)', color: '#c0392b', borderRadius: 8, padding: '10px 12px', marginTop: 12, fontSize: '0.9rem', fontWeight: 500 },
  bookmark: { color: 'rgba(0,0,0,0.4)', fontSize: '0.8rem', lineHeight: 1.5, textAlign: 'center', margin: '20px 0 0' },
  bookmarkUrl: { color: BURGUNDY, fontWeight: 600, textDecoration: 'none' },
  saved: { background: 'rgba(39,174,96,0.08)', border: '1px solid rgba(39,174,96,0.3)', color: '#1e7e44', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: '0.95rem', fontWeight: 500 },
  recent: { marginTop: 16, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 20, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' },
  recentTitle: { color: '#888', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 },
  recentRow: { display: 'flex', alignItems: 'baseline', gap: 10, padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: '0.9rem' },
  recentDate: { color: '#999', width: 86, flexShrink: 0 },
  recentReason: { flex: 1, color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  recentAmt: { color: BURGUNDY, fontWeight: 600 },
};
